import React, { useCallback, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import AvatarGroup from '@atlaskit/avatar-group';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, Pressable, xcss } from '@atlaskit/primitives';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePageSpaceKey } from '@confluence/page-context';
import { ShareAndRestrictDialogController } from '@confluence/share-and-restrict-dialog/entry-points/ShareAndRestrictDialogController';
import { fg } from '@confluence/feature-gating';
import { TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component';
import { useIsGuest } from '@confluence/session-data/entry-points/useAccessStatus';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { isExternalShareRequest } from '@confluence/external-share-utils/entry-points/isExternalShareRequest';

import { MAX_GROUP_SIZE } from '../presenceUtils';
import type { Participant, PresenceContentMode, PresenceContentType } from '../presenceTypes';

import { useIsEligibleForSuggestions } from './useIsEligibleForSuggestions';
import { NoCollaboratorsDialog } from './NoCollaboratorsDialog';
import { SuggestedCollaboratorsDialog } from './SuggestedCollaboratorsDialog';
import { PresenceAvatarButton } from './PresenceAvatarButton';
import { PresenceMenuWithGuest } from './PresenceMenuWithGuest';
import { AddPeopleButton } from './AddPeopleButton';
import { FlippedAnimationsWrapper } from './FlippedAnimationsWrapper';

const i18n = defineMessages({
	overflowLabel: {
		id: 'team-presence.avatar-group.overflow-label',
		defaultMessage: 'Overflow menu',
		description: 'Accessible label for presence avatar group overflow menu',
	},
	blogpostLandmark: {
		id: 'team-presence.avatar-group.blogpost-landmark',
		defaultMessage: 'Active people on blog post',
		description: 'Accessible region landmark for presence avatar group when on a blog post',
	},
	pageLandmark: {
		id: 'team-presence.avatar-group.page-landmark',
		defaultMessage: 'Active people on page',
		description: 'Accessible region landmark for presence avatar group when on a page',
	},
});

export type PresenceAvatarGroupProps = {
	contentId: string;
	contentMode: PresenceContentMode;
	contentType: PresenceContentType;
	participants: Participant[];
	analyticSource?: string;
	popupOpen?: boolean;
	totalParticipants: number;
};
const popupContainerStyles = xcss({
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
	borderRadius: 'radius.small',
	position: 'relative',
	width: '360px',
});

const editObjectHeaderStyles = xcss({
	paddingTop: 'space.0',
	paddingBottom: 'space.0',
});

const overflowMoreStyles = xcss({
	padding: 'space.075',
	backgroundColor: 'color.background.accent.gray.subtler',
	borderRadius: 'radius.full',
	color: 'color.text.subtle',
	font: 'font.heading.xxsmall',
	':hover': {
		backgroundColor: 'color.background.accent.gray.subtler.hovered',
	},
	':active': {
		backgroundColor: 'color.background.accent.gray.subtler.pressed',
	},
});

const moreActiveStyles = xcss({
	backgroundColor: 'color.background.selected',
	color: 'color.text.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

// Base styles for indicator - margin will be conditionally applied based on feature gate
const indicatorBaseStyles = xcss({
	minWidth: '28px',
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
	borderRadius: 'radius.small',
	':focus-visible': {
		outlineColor: 'color.border.focused',
		outlineStyle: 'solid',
		outlineWidth: 'border.width.focused',
		outlineOffset: 'space.050',
	},
});

// Legacy margin styles (used when platform spacing fix is disabled)
const indicatorLegacyMarginStyles = xcss({
	margin: 'space.025',
});

const indicatorOverflowStyles = xcss({
	minWidth: '35px',
	maxWidth: '35px',
});

export const PresenceAvatarGroup = ({
	contentId,
	contentMode,
	contentType,
	analyticSource,
	popupOpen,
	participants,
	totalParticipants,
}: PresenceAvatarGroupProps) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { isLoggedIn } = useSessionData();
	const isGuest = useIsGuest();
	const isExternal = isExternalShareRequest();
	const [spaceKey] = usePageSpaceKey();

	const [isPopupOpen, setIsPopupOpen] = useState(popupOpen || false);
	const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

	const isCollabExperimentEnabled = useIsEligibleForSuggestions(
		participants,
		isLoggedIn,
		isGuest,
		isExternal,
	);

	const isEligibleForEmptyPresenceDialog = useCallback(() => {
		// only 1 user, not anonymous and not guest user, and the collab experiment is not enabled
		return (
			participants.length === 1 &&
			isLoggedIn &&
			!isGuest &&
			!isExternal &&
			!isCollabExperimentEnabled
		);
	}, [participants, isLoggedIn, isGuest, isExternal, isCollabExperimentEnabled]);

	useEffect(() => {
		if (
			isPopupOpen &&
			participants.length > 1 &&
			participants.length <= MAX_GROUP_SIZE &&
			!isCollabExperimentEnabled
		) {
			setIsPopupOpen(false);
		}
	}, [isPopupOpen, participants, isCollabExperimentEnabled]);

	const handleShareClick = useCallback(
		(analyticsTriggerSource: string) => {
			setIsPopupOpen(false);
			const shareDialogType = 'v2';
			setIsShareDialogOpen(true);
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: analyticsTriggerSource,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'presenceShare',
					attributes: {
						contentId,
						contentMode,
						contentType,
						shareDialog: shareDialogType,
					},
				},
			}).fire();
		},
		[contentId, contentMode, contentType, createAnalyticsEvent],
	);

	const toggleEmptyPresenceDialog = useCallback(() => {
		setIsPopupOpen((isPopupOpenState) => !isPopupOpenState);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: analyticSource,
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'singlePresenceAvatarGroup',
				product: 'confluence',
				attributes: {
					contentId,
					contentMode,
					contentType,
					toggle: isPopupOpen ? 'close' : 'open',
				},
			},
		}).fire();
	}, [contentId, contentMode, contentType, analyticSource, createAnalyticsEvent, isPopupOpen]);

	const isSingleParticipant = Boolean(participants && totalParticipants === 1);

	const onMoreClick = useCallback(
		(e: React.MouseEvent<Element, MouseEvent>, number: number) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: analyticSource,
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'presenceAvatarGroupMoreButton',
					product: 'confluence',
					attributes: {
						contentId,
						contentType,
						contentMode,
						number,
					},
				},
			}).fire();

			setIsPopupOpen((isPopupOpenState) => !isPopupOpenState);

			// platform-design-system-popup-ref causes the popup to close when the user clicks on the more button,
			// revisit what other types of changes we can put in place to not have to call stopPropagation
			e.stopPropagation();
		},
		[contentId, contentType, contentMode, analyticSource, createAnalyticsEvent],
	);

	const getTriggerId = () => {
		if (isCollabExperimentEnabled) {
			return 'suggested-collaborators-dialog';
		}
		return isSingleParticipant ? 'no-collaborators-dialog' : 'presence-menu';
	};

	const triggerId = getTriggerId();

	const renderSuggestedCollaboratorsDialog = () => (
		<SuggestedCollaboratorsDialog
			contentMode={contentMode}
			contentId={contentId}
			contentType={contentType}
			onShareClick={handleShareClick}
			spaceKey={spaceKey || ''}
			testId={triggerId}
		/>
	);

	const renderNoCollaboratorsDialog = () => (
		<NoCollaboratorsDialog
			contentId={contentId}
			contentMode={contentMode}
			contentType={contentType}
			spaceKey={spaceKey || ''}
			onShareClick={handleShareClick}
			testId={triggerId}
		/>
	);

	const renderPresenceMenu = () => (
		<PresenceMenuWithGuest
			contentId={contentId}
			contentType={contentType}
			contentMode={contentMode}
			participants={participants}
			totalParticipants={totalParticipants}
		/>
	);

	const getPopUpContent = () => {
		if (isCollabExperimentEnabled) {
			return renderSuggestedCollaboratorsDialog();
		}

		return isSingleParticipant ? renderNoCollaboratorsDialog() : renderPresenceMenu();
	};

	const PopupContent = getPopUpContent();

	const overflowParticipantCount = totalParticipants - MAX_GROUP_SIZE + 1;

	const shouldRenderAddPersonButton = isCollabExperimentEnabled && overflowParticipantCount <= 0;

	useEffect(() => {
		if (shouldRenderAddPersonButton) {
			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					name: 'addCollaboratorsButton',
					attributes: {
						contentId,
						contentType,
						contentMode,
					},
				},
			}).fire();
		}
	}, [shouldRenderAddPersonButton, contentId, contentType, contentMode, createAnalyticsEvent]);

	const landmarkRegionLabel =
		contentType === 'blogpost'
			? formatMessage(i18n.blogpostLandmark)
			: formatMessage(i18n.pageLandmark);

	const isEditObjectHeader =
		fg('confluence_frontend_content_wrapper') && (contentMode === 'edit' || contentMode === 'live');

	return (
		<>
			<ExperienceSuccess name={TEAM_PRESENCE_AVATAR_GROUP_VIEW_EXPERIENCE} />
			{(participants.length === 1 || shouldRenderAddPersonButton) && isShareDialogOpen && (
				<ShareAndRestrictDialogController
					contentId={contentId}
					contentType={contentType}
					allowRestrictions
					pageModes="view"
					refetchButtonData={() => {}}
					onClose={() => {
						setIsShareDialogOpen(false);
					}}
					isUnpublishedDraft={false}
				/>
			)}
			<Popup
				isOpen={isPopupOpen}
				onClose={() => setIsPopupOpen(false)}
				placement="bottom-end"
				content={() => <Box xcss={popupContainerStyles}>{PopupContent}</Box>}
				trigger={(triggerProps) => (
					<Box
						{...triggerProps}
						paddingInline="space.050"
						paddingBlock="space.025"
						aria-label={landmarkRegionLabel}
						role="region"
						xcss={isEditObjectHeader && editObjectHeaderStyles}
						data-vc="presence-avatar-group"
					>
						<AvatarGroup
							appearance="stack"
							size="small"
							maxCount={MAX_GROUP_SIZE}
							data={participants}
							testId="presence-avatar-group"
							label={landmarkRegionLabel}
							borderColor={token('color.background.neutral')}
							onMoreClick={(e) => onMoreClick(e, overflowParticipantCount)}
							shouldPopupRenderToParent={false}
							isTooltipDisabled
							overrides={{
								Avatar: {
									render: (_, props, index) => {
										return (
											<FlippedAnimationsWrapper flipId={`PresenceAvatarButton-${index}`}>
												<Flex alignItems="center">
													<PresenceAvatarButton
														key={`PresenceAvatarButton-${index}`}
														index={index}
														participant={participants[index]}
														contentMode={contentMode}
														contentType={contentType}
														contentId={contentId}
														isSingleParticipant={isSingleParticipant}
														isEligibleForEmptyPresenceDialog={isEligibleForEmptyPresenceDialog()}
														toggleEmptyPresenceDialog={toggleEmptyPresenceDialog}
														avatarProps={props}
														analyticSource={analyticSource}
													/>
													{shouldRenderAddPersonButton && index === participants.length - 1 && (
														<AddPeopleButton
															contentType={contentType}
															contentMode={contentMode}
															contentId={contentId}
															toggleEmptyPresenceDialog={toggleEmptyPresenceDialog}
															isPopupOpen={isPopupOpen}
															analyticsTriggerSource={analyticSource}
														/>
													)}
												</Flex>
											</FlippedAnimationsWrapper>
										);
									},
								},
								MoreIndicator: {
									render: (_, { buttonProps, isActive, borderColor, ...rest }) => (
										<FlippedAnimationsWrapper flipId={`more-indicator`}>
											<Pressable
												type="submit"
												{...buttonProps}
												{...rest}
												padding={fg('platform-avatar-group-spacing-fix') ? 'space.0' : 'space.025'}
												onClick={rest.onClick}
												xcss={[
													indicatorBaseStyles,
													!fg('platform-avatar-group-spacing-fix') && indicatorLegacyMarginStyles,
													// TODO: This is a bug, anything above 1000 will break...
													overflowParticipantCount > 100 && indicatorOverflowStyles,
												]}
												testId={rest.testId}
												data-vc="presence-overflow"
												aria-label={formatMessage(i18n.overflowLabel)}
											>
												<Box xcss={[overflowMoreStyles, isPopupOpen && moreActiveStyles]}>
													{overflowParticipantCount}
												</Box>
											</Pressable>
										</FlippedAnimationsWrapper>
									),
								},
							}}
						/>
					</Box>
				)}
			/>
		</>
	);
};
